import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/Download';
import ErrorIcon from '@mui/icons-material/Error';
import LockIcon from '@mui/icons-material/Lock';
import RefreshIcon from '@mui/icons-material/Refresh';
import SettingsIcon from '@mui/icons-material/Settings';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WarningIcon from '@mui/icons-material/Warning';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import StatCard from '../../lib/components/StatCard';
import { useTimetableStore } from '../../stores/timetableStore';
import {
  formatRelativeTime,
  getDashboardSummary,
  mockDashboardStats,
  mockExtendedStats,
  mockQuickActions,
  mockRecentActivity,
  mockSystemStatus,
  mockTimetableProgress,
  type QuickAction,
} from './MockData';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { currentTimetable } = useTimetableStore();

  const summary = useMemo(() => getDashboardSummary(), []);

  const getQuickActionIcon = (icon: string) => {
    switch (icon) {
      case 'generate':
        return <AutoFixHighIcon />;
      case 'view':
        return <VisibilityIcon />;
      case 'conflict':
        return <WarningIcon />;
      case 'export':
        return <DownloadIcon />;
      case 'finalize':
        return <LockIcon />;
      case 'settings':
        return <SettingsIcon />;
      default:
        return <SettingsIcon />;
    }
  };

  const getActivityIcon = (entityType: string) => {
    switch (entityType) {
      case 'conflict':
        return <WarningIcon color="warning" />;
      case 'timetable':
        return <CheckCircleIcon color="success" />;
      case 'teacher':
        return <CheckCircleIcon color="info" />;
      case 'rule':
        return <SettingsIcon color="action" />;
      case 'simulation':
        return <AutoFixHighIcon color="primary" />;
      case 'export':
        return <DownloadIcon color="secondary" />;
      case 'room':
        return <CheckCircleIcon color="info" />;
      default:
        return <CheckCircleIcon color="action" />;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
        return <CheckCircleIcon color="success" fontSize="small" />;
      case 'warning':
        return <WarningIcon color="warning" fontSize="small" />;
      case 'error':
        return <ErrorIcon color="error" fontSize="small" />;
      default:
        return <CheckCircleIcon color="action" fontSize="small" />;
    }
  };

  const getProgressColor = (status: string): 'success' | 'warning' | 'error' | 'info' | 'primary' => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in-progress':
        return 'primary';
      case 'pending':
        return 'info';
      case 'blocked':
        return 'error';
      default:
        return 'primary';
    }
  };

  const handleQuickAction = (action: QuickAction) => {
    navigate(action.route);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Welcome back! Here's an overview of your timetable status.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Chip
            label={`Version ${summary.currentVersion}`}
            color="primary"
            size="small"
          />
          <Tooltip title="Refresh dashboard">
            <IconButton>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {mockDashboardStats.map((stat, index) => (
          <Grid item key={index} xs={12} sm={6} md={3}>
            <StatCard stat={stat} />
          </Grid>
        ))}
      </Grid>

      {/* Main Content Grid */}
      <Grid container spacing={3}>
        {/* Left Column - Progress & Quick Actions */}
        <Grid item xs={12} lg={8}>
          {/* Progress Overview */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Timetable Progress</Typography>
                <Chip
                  label={`${summary.overallProgress}% Complete`}
                  color="primary"
                  size="small"
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <LinearProgress
                  variant="determinate"
                  value={summary.overallProgress}
                  sx={{ height: 10, borderRadius: 1 }}
                />
              </Box>

              <Grid container spacing={2}>
                {mockTimetableProgress.map((phase, index) => (
                  <Grid item key={index} xs={12} sm={6}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        bgcolor: phase.status === 'completed' ? 'success.50' : undefined,
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {phase.phase}
                        </Typography>
                        <Chip
                          label={phase.status.replace('-', ' ')}
                          size="small"
                          color={getProgressColor(phase.status)}
                          variant={phase.status === 'completed' ? 'filled' : 'outlined'}
                        />
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={phase.progress}
                        color={getProgressColor(phase.status)}
                        sx={{ height: 6, borderRadius: 1, mb: 1 }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {phase.description}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Quick Actions
              </Typography>
              <Grid container spacing={2}>
                {mockQuickActions.map((action) => (
                  <Grid item key={action.id} xs={6} sm={4}>
                    <Card
                      variant="outlined"
                      sx={{
                        height: '100%',
                        '&:hover': {
                          borderColor: `${action.color}.main`,
                          bgcolor: 'action.hover',
                        },
                      }}
                    >
                      <CardActionArea
                        onClick={() => handleQuickAction(action)}
                        disabled={action.disabled}
                        sx={{ height: '100%', p: 2 }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            textAlign: 'center',
                          }}
                        >
                          <Avatar
                            sx={{
                              bgcolor: `${action.color}.light`,
                              color: `${action.color}.main`,
                              mb: 1,
                            }}
                          >
                            {getQuickActionIcon(action.icon)}
                          </Avatar>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {action.title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {action.description}
                          </Typography>
                        </Box>
                      </CardActionArea>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column - Activity & Status */}
        <Grid item xs={12} lg={4}>
          {/* Recent Activity */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Recent Activity</Typography>
                <Button size="small">View All</Button>
              </Box>
              <List dense sx={{ maxHeight: 400, overflow: 'auto' }}>
                {mockRecentActivity.slice(0, 6).map((activity, index) => (
                  <React.Fragment key={activity.id}>
                    <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                      <ListItemAvatar>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: 'action.selected' }}>
                          {getActivityIcon(activity.entityType)}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" fontWeight="medium">
                              {activity.action}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatRelativeTime(activity.timestamp)}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {activity.details}
                          </Typography>
                        }
                      />
                    </ListItem>
                    {index < 5 && <Divider component="li" />}
                  </React.Fragment>
                ))}
              </List>
            </CardContent>
          </Card>

          {/* Key Metrics */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Key Metrics
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Teachers Assigned
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" fontWeight="bold">
                      {mockExtendedStats.resources.teachersAssigned}/{mockExtendedStats.resources.teachersTotal}
                    </Typography>
                    <TrendingUpIcon color="success" fontSize="small" />
                  </Box>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={(mockExtendedStats.resources.teachersAssigned / mockExtendedStats.resources.teachersTotal) * 100}
                  color="success"
                  sx={{ height: 6, borderRadius: 1 }}
                />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Room Utilization
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" fontWeight="bold">
                      {mockExtendedStats.resources.utilizationRate}%
                    </Typography>
                    <TrendingUpIcon color="success" fontSize="small" />
                  </Box>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={mockExtendedStats.resources.utilizationRate}
                  color="info"
                  sx={{ height: 6, borderRadius: 1 }}
                />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Conflicts Resolved
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" fontWeight="bold">
                      {mockExtendedStats.conflicts.resolved}/{mockExtendedStats.conflicts.resolved + mockExtendedStats.conflicts.pending}
                    </Typography>
                    <TrendingDownIcon color="warning" fontSize="small" />
                  </Box>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={
                    (mockExtendedStats.conflicts.resolved /
                      (mockExtendedStats.conflicts.resolved + mockExtendedStats.conflicts.pending)) *
                    100
                  }
                  color="warning"
                  sx={{ height: 6, borderRadius: 1 }}
                />
              </Box>
            </CardContent>
          </Card>

          {/* System Status */}
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                System Status
              </Typography>
              <List dense>
                {mockSystemStatus.map((status, index) => (
                  <ListItem key={index} sx={{ px: 0 }}>
                    <ListItemAvatar sx={{ minWidth: 36 }}>
                      {getStatusIcon(status.status)}
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="body2" fontWeight="medium">
                          {status.component}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          {status.message}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;

